/**
 * /segments — list segments with quick actions to edit / view contacts
 * / delete. New segments come from /segments/new.
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { useAuth } from "../../../lib/auth-context.js";

interface SegmentRow {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export const Route = createFileRoute("/_app/segments/")({
  component: SegmentsList,
});

function SegmentsList() {
  const { activeIgAccount } = useAuth();
  const [rows, setRows] = useState<SegmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeIgAccount) return;
    void load();
  }, [activeIgAccount?.id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ ig_account_id: activeIgAccount!.id });
      const r = await api.get<{ segments: SegmentRow[] }>(`/segments?${params}`);
      setRows(r.data.segments);
    } catch {
      setError("Failed to load segments");
    } finally {
      setLoading(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this segment?")) return;
    try {
      await api.delete(`/segments/${id}`);
      setRows((cur) => cur.filter((s) => s.id !== id));
    } catch {
      setError("Failed to delete");
    }
  }

  return (
    <section style={{ maxWidth: 880, margin: "0 auto" }}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 28 }}>Segments</h1>
        <Link to="/segments/new" className="brut-button">
          + New segment
        </Link>
      </header>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Saved filters over your contacts. Use them to enumerate audiences for
        broadcasts and trigger conditions.
      </p>

      {error ? (
        <div className="brut-card" style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      {loading ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="brut-card" style={{ textAlign: "center", padding: 24 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 700 }}>No segments yet</p>
          <p style={{ margin: 0, fontSize: 14, color: "var(--muted)" }}>
            Segments are saved contact filters — "tagged 'vip' AND opted-in
            for marketing", etc. Build one above ↑ then point a broadcast
            at it.
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
          {rows.map((s) => (
            <li
              key={s.id}
              className="brut-card"
              style={{
                padding: 14,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <Link
                  to="/segments/$segmentId"
                  params={{ segmentId: s.id }}
                  style={{ fontWeight: 800, fontSize: 16, textDecoration: "none" }}
                >
                  {s.name}
                </Link>
                {s.description ? (
                  <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 2 }}>
                    {s.description}
                  </div>
                ) : null}
                <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 4 }}>
                  Updated {new Date(s.updatedAt).toLocaleString()}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Link
                  to="/contacts"
                  search={{ segmentId: s.id }}
                  className="brut-button is-secondary"
                  style={{ fontSize: 13, padding: "6px 10px" }}
                >
                  Contacts
                </Link>
                <button
                  onClick={() => void remove(s.id)}
                  className="brut-button is-danger"
                  style={{ fontSize: 13, padding: "6px 10px" }}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
